import React from 'react';
import { createId } from '@paralleldrive/cuid2';
import { isEmpty, isNil, noop } from 'lodash/fp';
import { HORPADDING, BORDERWIDTH, default as styles } from '../StandardCard/StandardCard.styles';
import { ContainerPayload } from 'src/components/RightContainer/RightContainer.slice';
import { RightContainerPayloadType } from 'src/components/RightContainer/RightContainer';
import { classes } from 'typestyle';
import collCardStyles, {
  COLLCARD_WIDTH,
  COLLCARD_HEIGHT,
  ANCHOR_IMG_W,
  ANCHOR_IMG_H,
  ITEM_THUMB_SIZE,
} from './CollectionCard.styles';
import { CardViewCardColumn } from '../UIData.types';
import CenteredImage from 'src/components/CenteredImage/CenteredImage';
import { IS_HINDSIGHTING } from 'src/utils/Domain/ConstantsFunctions';
import { getSectionContext } from 'src/utils/Domain/Perspective';
import { HINDSIGHTING_CONTEXT } from 'src/utils/Domain/Constants';
import { AdornmentType } from 'configuration-schemas/viewdefns/literals';
import Adornments, { AdornmentContextOptions } from 'src/components/Adornments/Adornments';
import { ContextMenuType } from 'src/components/WorklistContextMenu/WorklistContextMenu';
import Chip from '@material-ui/core/Chip';
import { StandardCardProps } from '../StandardCard/StandardCard';
import { ConfigurableGridButton, ConfigurableGridLinkButton } from 'configuration-schemas/viewdefns/general';
import { getUrl, processApiParams } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';

export interface CollectionItem {
  imgSrc: string;
  name: string;
  category: string;
  id?: string;
}

export interface Props extends StandardCardProps {
  // Identity
  id: string;
  name: string;
  description: string;
  styleId: string;
  leafId?: string;

  // Standard card passthrough
  isBlankCard?: boolean;
  isPrintMode?: boolean;
  stylePaneKey?: string;
  adornments: AdornmentType[];

  // Collection-specific
  collectionItems?: CollectionItem[]; // TODO

  // Callbacks
  onItemClick?: (item: ContainerPayload) => void;
  onMoreInfoClick?: (item: ContainerPayload) => void;

  // Required by shared card props pipeline
  imgSrc: string;
  stars: number;
  styleName: string;
  styleDescription: string;
  value: number | string;
  columns?: CardViewCardColumn[];
  departmentId?: string;
  swatchIds?: string[];
  popoverTitle?: string;

  /** Added to support link button ONLY in this card for outfit things */
  buttons?: ConfigurableGridButton[];
}

interface State {
  targetId: string;
}

export function executeItemClick(props: Props): void {
  const { selectable, onSelectItem } = props;
  if (selectable && onSelectItem) {
    // TODO replace the type used by `onSelectItem` with a type that
    // works for props, or just remove this and move it into `onItemClick`
    onSelectItem(props as any);
    return;
  }
  // copied from standardcard
  if (isNil(props.onItemClick)) {
    return;
  }
  const currentContext = getSectionContext();
  const isHindsighting = IS_HINDSIGHTING(currentContext);
  const type =
    currentContext === HINDSIGHTING_CONTEXT
      ? RightContainerPayloadType.History
      : RightContainerPayloadType.Assortment;
  props.onItemClick({
    type,
    id: props.id,
    parentId: props.styleId,
    isAssortmentBuild: !isHindsighting,
  });
}

export function buildContextOptions(props: Props): AdornmentContextOptions {
  const { onItemClick, stylePaneKey } = props;
  const isHindsighting = IS_HINDSIGHTING(stylePaneKey);
  const containerPayload: ContainerPayload = {
    type: RightContainerPayloadType.Assortment,
    id: props.leafId || props.id,
    parentId: props.styleId,
    isAssortmentBuild: !isHindsighting,
  };
  const openStylePane = !isNil(onItemClick) ? onItemClick.bind(null, containerPayload) : noop;
  return {
    type: ContextMenuType.card,
    parentIdentityField: 'styleId',
    onOpenStylePane: openStylePane,
    nodeData: props,
  };
}

export function executeLinkButtonClick(props: Props, btn: ConfigurableGridLinkButton, e: React.MouseEvent): void {
  e.stopPropagation();
  const processedApi = processApiParams(btn.params.dataApi, props);
  const url = getUrl(processedApi);
  window.location.hash = url;
}

export class CollectionCard extends React.Component<Props, State> {
  static calcCardWidth = () => COLLCARD_WIDTH + HORPADDING;
  static calcCardHeight = () => COLLCARD_HEIGHT + BORDERWIDTH * 2;

  constructor(props: Props) {
    super(props);
    this.state = {
      targetId: createId(),
    };
  }

  handleItemClick = () => executeItemClick(this.props);

  getContextOptions(): AdornmentContextOptions {
    return buildContextOptions(this.props);
  }

  handleLinkButtonClick = (btn: ConfigurableGridLinkButton, e: React.MouseEvent) =>
    executeLinkButtonClick(this.props, btn, e);

  render() {
    const { id, name, description, isPrintMode, isSelected, adornments, imgSrc, subname, children, collectionType, buttons } = this.props;
    const renderAdornments = !isEmpty(adornments);
    const contextOptions = this.getContextOptions();
    const hoverStyle = styles.getHoverStyle(!!this.props.isBlankCard);
    const linkButtons = (buttons ?? []).filter((btn): btn is ConfigurableGridLinkButton => btn.type === 'link');
    return (
      <div
        id={this.state.targetId}
        className={classes(collCardStyles.card, hoverStyle)}
        data-qa-component="CollectionCard"
        data-qa-action="OpenPopover"
        onClick={this.handleItemClick}
        data-qa-key={id}
      >
        {isSelected && (
          <React.Fragment>
            <div className={collCardStyles.cardOverlay} />
            <i className={`fas fa-check-circle ${collCardStyles.iconOverlay}`} />
          </React.Fragment>
        )}
        {/* Zone 1: Header */}
        <div className={collCardStyles.header}>
          <h4 className={collCardStyles.headerTitle}>
            {renderAdornments && <Adornments adornments={adornments} productId={id} contextOptions={contextOptions} />}
            <span>{name}</span>
          </h4>
          <div className={collCardStyles.headerSubTitle}>
            <Chip label={collectionType} size="small" className={collCardStyles.anchorBadge} />
            {subname ? <p className={collCardStyles.headerDesc}>{subname}</p> : <></>}
          </div>
        </div>

        {/* Zone 2: Anchor Piece */}
        <div>
          <div className={collCardStyles.anchorSubCard}>
            <CenteredImage src={imgSrc} width={ANCHOR_IMG_W} height={ANCHOR_IMG_H} />
            <div className={collCardStyles.anchorInfo}>
              <span className={collCardStyles.anchorName}>{name}</span>
              <span className={collCardStyles.anchorCategory}>{description}</span>
          {linkButtons.length > 0 && (
            <div style={{justifySelf: 'end'}}>
              {linkButtons.map((btn, i) => (
                <button
                  key={i}
                  className={collCardStyles.linkButton}
                  onClick={(e) => this.handleLinkButtonClick(btn, e)}
                >
                  {btn.icon && <i className={btn.icon} style={{ marginRight: 4 }} />}
                  {btn.text}
                </button>
              ))}
            </div>
          )}
            </div>
          </div>
        </div>

        {/* Zone 3: Complete the Look */}
        <div className={collCardStyles.sectionLabel}>SUPPORTING ITEMS</div>
          <div className={collCardStyles.itemsRow}>
            {(children ?? []).map((item, index) => (
              <div key={item.id || index} className={collCardStyles.itemCard}>
                <div className={collCardStyles.itemImageContainer}>
                  <CenteredImage src={item.imgSrc} width={ITEM_THUMB_SIZE} height={ITEM_THUMB_SIZE} />
                </div>
                <span className={collCardStyles.itemCategory}>{item.name}</span>
                <span className={collCardStyles.itemName}>{item.description}</span>
              </div>
            ))}
          </div>
      </div>
    );
  }
}
